import { useState } from "react";
import { Pencil, Plus, Power, Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, digits, errMsg, maskPhone } from "@/lib/api";
import { cn } from "@/lib/utils";
import { CourierForm } from "./CourierForm";

const matches = (c, q) => {
  const t = q.trim().toLowerCase();
  const d = digits(q);
  return !t || c.name.toLowerCase().includes(t) || (d && [c.document, c.phone, c.whatsapp].some((v) => (v || "").includes(d)));
};

function CourierRow({ c, sub, boss, onEdit, onToggle, onAddLinked }) {
  return (
    <li className={cn("flex flex-wrap items-center gap-3 px-4 py-3 text-sm", sub && "bg-muted/30 pl-6 sm:pl-10")} data-testid={`courier-row-${c.id}`}>
      <span className="min-w-0 flex-1">
        <span className="block font-medium">{c.name}</span>
        <span className="text-xs text-muted-foreground">{maskPhone(c.phone || "") || "sem telefone"}{boss ? ` · vinculado a ${boss.name}` : ""}{c.company ? ` · ${c.company}` : ""}</span>
      </span>
      <span className={cn("rounded px-2 py-0.5 text-xs font-medium", c.active ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-muted text-muted-foreground")} data-testid={`courier-status-${c.id}`}>{c.active ? "Ativo" : "Inativo"}</span>
      <span className="flex w-full shrink-0 flex-wrap gap-1 sm:w-auto">
        {!sub && !c.responsible_id && <Button size="sm" variant="ghost" onClick={() => onAddLinked(c)} title="Cadastrar outro entregador vinculado a este responsável" data-testid={`courier-add-linked-${c.id}`}><UserPlus className="mr-1 h-4 w-4" />Vincular</Button>}
        <Button size="sm" variant="ghost" onClick={() => onEdit(c)} data-testid={`courier-edit-${c.id}`}><Pencil className="mr-1 h-4 w-4" />Editar</Button>
        <Button size="sm" variant="ghost" onClick={() => onToggle(c)} data-testid={`toggle-courier-${c.id}`}><Power className="mr-1 h-4 w-4" />{c.active ? "Desativar" : "Ativar"}</Button>
      </span>
    </li>
  );
}

function CourierList({ couriers, onNew, ...rowProps }) {
  const [q, setQ] = useState("");
  const byId = Object.fromEntries(couriers.map((c) => [c.id, c]));
  const flat = q.trim() ? couriers.filter((c) => matches(c, q)) : null;
  const tops = couriers.filter((c) => !c.responsible_id || !byId[c.responsible_id]);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-md border bg-background px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome, CPF, CNPJ ou telefone" className="h-full min-w-0 flex-1 bg-transparent text-sm outline-none" data-testid="courier-search-input" />
        </label>
        <Button className="h-11" onClick={onNew} data-testid="courier-new-btn"><Plus className="mr-1 h-4 w-4" />Novo entregador</Button>
      </div>
      <ul className="max-h-[55vh] divide-y overflow-y-auto rounded-lg border" data-testid="courier-list">
        {flat ? flat.map((c) => <CourierRow key={c.id} c={c} boss={byId[c.responsible_id]} {...rowProps} />)
          : tops.map((c) => [<CourierRow key={c.id} c={c} {...rowProps} />, ...couriers.filter((s) => s.responsible_id === c.id).map((s) => <CourierRow key={s.id} c={s} sub boss={c} {...rowProps} />)])}
        {!(flat || couriers).length && <li className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="courier-list-empty">{flat ? "Nenhum entregador encontrado." : "Nenhum entregador cadastrado."}</li>}
      </ul>
    </div>
  );
}

export function CouriersDialog({ open, onOpenChange, couriers, reload }) {
  const [form, setForm] = useState(null);
  const toggle = async (c) => {
    try {
      await api.patch(`/couriers/${c.id}/active`, { active: !c.active });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const close = (o) => { onOpenChange(o); if (!o) setForm(null); };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[92vh] max-w-3xl grid-cols-[minmax(0,1fr)] overflow-y-auto" data-testid="couriers-modal">
        <DialogHeader><DialogTitle>{form ? (form.courier ? `Editar ${form.courier.name}` : "Novo entregador") : "Entregadores"}</DialogTitle></DialogHeader>
        {form ? <CourierForm key={form.courier?.id || form.responsible?.id || "new"} {...form} onBack={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />
          : <CourierList couriers={couriers} onNew={() => setForm({})} onEdit={(c) => setForm({ courier: c, responsible: couriers.find((x) => x.id === c.responsible_id) })}
              onToggle={toggle} onAddLinked={(c) => setForm({ responsible: c })} />}
      </DialogContent>
    </Dialog>
  );
}
